import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { type AttributeTreeNode } from '../components/generation/state/useGenerationState';
import { useGenerationSettings } from '../components/generation/state/settingsContext';
import { usePromptAttributes } from '../components/generation/hooks/usePromptAttributes';
import {
  TEMPLATE_SECTION_KEY,
  isCaptureRequiredOption,
  readPendingLoginGeneration,
  stashPendingLoginGeneration,
  findNodeByKey,
  captureTemplateTransientSettings,
  CAPTURE_TEMPLATE_TRANSIENT_KEYS,
} from '../components/generation/utils/templateCapture';
import { GenerationPreviewPanel } from '../components/generation/preview/GenerationPreviewPanel';
import { GenerateButton } from '../components/generation/common/GenerateButton';
import { MenuContent } from '../components/navigation/MenuContent';
import { UsodekiPageOverlay } from '../components/navigation/UsodekiPageOverlay';
import { usePageOverlayStore } from '../stores/pageOverlayStore';
import { usePartnerBranding } from '../hooks/usePartnerBranding';
import { usePublicHomeBranding } from '../hooks/usePublicHomeBranding';
import { renderAccentText } from '../utils/renderAccentText';
import { useCredits } from '../hooks/useCredits';
import { useUser } from '../features/auth/useUser';
import { useAuthToken } from '../features/auth/useAuthToken';
import { useLogout } from '../hooks/useLogout';
import { hardNavigate } from '../utils/navigation';
import { brandConfig } from '../config/brands';
import { logLeadEvent, logLeadEventBeacon, linkLeadEvents } from '../features/leads/leadEvents';
import MaterialsFloatingOpenButton from '../components/panels/MaterialsFloatingOpenButton';
// 生成り紙に直接浮かせる透過版ロゴ (背景白を抜いたホーム専用アセット)
import usodekiLogoTransparent from '../assets/usodeki_logo_transparent.png';
import './UsodekiHomePage.css';

type TemplateChip = {
  groupKey: string;
  option: AttributeTreeNode;
};

type TemplateCategory = {
  id: string;
  label: string;
  chips: TemplateChip[];
};

/** option ノードの metadata.thumbnail_url (任意)。DB 側で設定すればタイルに画像が出る */
function thumbnailUrlOf(node: AttributeTreeNode): string | undefined {
  const url = node.metadata?.thumbnail_url;
  return typeof url === 'string' && url ? url : undefined;
}

// ---- キャプチャ必須テンプレの URL 入力: プレフィックスと localStorage メモ ----
// X / ホームページとも scheme 等のプレフィックスは固定表示し、ユーザーは {} 部分
// (ユーザー名 / ドメイン以降) だけを入力する。
const URL_PREFIXES = {
  x: 'https://x.com/',
  instagram: 'https://www.instagram.com/',
  homepage: 'https://',
} as const;
type UrlKind = keyof typeof URL_PREFIXES;
const URL_KINDS = Object.keys(URL_PREFIXES) as UrlKind[];

/** X ユーザー名 / Instagram ユーザー名 / ウェブサイト URL を端末に記憶して再訪時に復元するキー */
const URL_MEMO_STORAGE_KEY = 'usodeki-template-url-memo';
type UrlMemo = { kind: UrlKind; bodies: Record<UrlKind, string> };

function readUrlMemo(): UrlMemo | null {
  try {
    const raw = localStorage.getItem(URL_MEMO_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { kind?: unknown; bodies?: Record<string, unknown> } | null;
    const bodies = { x: '', instagram: '', homepage: '' } as Record<UrlKind, string>;
    for (const k of URL_KINDS) {
      const v = parsed?.bodies?.[k];
      if (typeof v === 'string') bodies[k] = v;
    }
    const kind = URL_KINDS.includes(parsed?.kind as UrlKind) ? (parsed?.kind as UrlKind) : 'x';
    return { kind, bodies };
  } catch {
    return null;
  }
}

function writeUrlMemo(memo: UrlMemo): void {
  try {
    localStorage.setItem(URL_MEMO_STORAGE_KEY, JSON.stringify(memo));
  } catch {
    // private mode 等で書けない場合は記憶しない (入力自体は機能する)
  }
}

/** カテゴリ配下の option を平坦化 ('none' は選択解除用なので除外) */
function collectChips(groupKey: string, children: AttributeTreeNode[] | undefined): TemplateChip[] {
  const out: TemplateChip[] = [];
  for (const child of children ?? []) {
    if (child.type === 'option' && child.key !== 'none') {
      out.push({ groupKey, option: child });
    } else if (child.children?.length) {
      out.push(...collectChips(groupKey, child.children));
    }
  }
  return out;
}

/**
 * usodeki (嘘でできた世界) 専用ホーム。
 * ヘッダー/フッター無し (appChrome=false) のモバイル特化・白ベース構成。
 * 生成 UI 共通のアコーディオンは使わず、テンプレート選択 (カード)・URL 入力・
 * 選択素材・生成ボタン/プレビューを専用デザインで縦に並べる。
 * 生成の実行・進行状況・結果表示は GenerationPreviewPanel を機能として再利用する。
 */
export default function UsodekiHomePage() {
  const { isAdmin } = useUser();
  const { token } = useAuthToken();
  // 公開ホーム: 未ログインでも閲覧可 (AuthGate が publicHome で `/` を素通しする)。
  // 認証必須 API は isLoggedIn でゲートし、生成はログイン誘導に差し替える。
  const isLoggedIn = !!token;
  const { data: creditsData } = useCredits({ enabled: isLoggedIn });
  // org-admin サイト設定の「トップページ文言」(タイトル/サブタイトル)。
  // 未ログイン時は認証不要の login-branding から同じ文言を引く。
  const partnerBranding = usePartnerBranding(isLoggedIn);
  const publicBranding = usePublicHomeBranding(!isLoggedIn);
  const homeHeroTitle = isLoggedIn ? partnerBranding.homeHeroTitle : publicBranding.homeHeroTitle;
  const homeHeroSubtitle = isLoggedIn ? partnerBranding.homeHeroSubtitle : publicBranding.homeHeroSubtitle;
  // 未ログイン時はテンプレートのみ返す公開エンドポイントへ (認証付きと同レスポンス形)
  const attributeQuery = usePromptAttributes(
    isLoggedIn ? undefined : { publicPartnerCode: brandConfig.id },
  );
  const {
    settings,
    setAttribute,
    setAttributeTree,
    applyTransientSettings,
    templateReferenceUrl,
    setTemplateReferenceUrl,
    pendingPrompt,
    clearPendingEdit,
  } = useGenerationSettings();

  const [isMenuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  // 利用状況/マイページのオーバーレイが開いたらメニューを閉じる
  const overlayActive = usePageOverlayStore((s) => s.active);
  const openOverlay = usePageOverlayStore((s) => s.open);
  useEffect(() => {
    if (overlayActive) setMenuOpen(false);
  }, [overlayActive]);

  // 属性ツリーを生成 state に流し込む (プロンプト組み立て・コスト計算が参照する)
  useEffect(() => {
    if (attributeQuery.data?.items?.length) {
      setAttributeTree(attributeQuery.data.items as AttributeTreeNode[]);
    }
  }, [attributeQuery.data, setAttributeTree]);

  // usodeki はアスペクト比を選ぶ UI を持たないため 4:5 (縦型) に固定する。
  // 既定は 'none' (モデル任せ) なので、4:5 以外なら矯正する (localStorage にも 4:5 が残る)。
  useEffect(() => {
    if (settings.aspect_ratio !== '4:5') {
      setAttribute('aspect_ratio', '4:5');
    }
  }, [settings.aspect_ratio, setAttribute]);

  // ログイン/新規登録後の初回着地で、未ログイン時に記録したリードを user_uid へ紐付ける。
  // usodeki はログイン後も新規登録後もこのホームへ着地するため、ここ 1 箇所で両方を拾える。
  const leadLinkAttemptedRef = useRef(false);
  useEffect(() => {
    if (!isLoggedIn || leadLinkAttemptedRef.current) return;
    leadLinkAttemptedRef.current = true;
    void linkLeadEvents();
  }, [isLoggedIn]);

  // メニュー領域外クリックで閉じる
  useEffect(() => {
    if (!isMenuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  const handleLogout = useLogout();

  // ---- テンプレート選択 ----

  const templateGroups = useMemo(() => {
    const items = (attributeQuery.data?.items ?? []) as AttributeTreeNode[];
    return items.filter((n) => n.type === 'group' && n.section === TEMPLATE_SECTION_KEY);
  }, [attributeQuery.data?.items]);

  const categories = useMemo<TemplateCategory[]>(() => {
    const out: TemplateCategory[] = [];
    for (const group of templateGroups) {
      for (const child of group.children ?? []) {
        if (child.type === 'group') {
          const chips = collectChips(group.key, child.children);
          if (chips.length) out.push({ id: child.id, label: child.label, chips });
        }
      }
      // カテゴリなしで直下に option が並ぶ構成もサポート
      const direct = (group.children ?? []).filter((c) => c.type === 'option' && c.key !== 'none');
      if (direct.length) {
        out.push({
          id: group.id,
          label: group.label,
          chips: direct.map((option) => ({ groupKey: group.key, option })),
        });
      }
    }
    return out;
  }, [templateGroups]);

  // 選択中テンプレート (attributes[group.key] が 'none' 以外の最初の group)
  const activeTemplate = useMemo(() => {
    for (const group of templateGroups) {
      const value = settings.attributes?.[group.key];
      if (typeof value === 'string' && value && value !== 'none') {
        const optionNode = findNodeByKey(group.children, value);
        return {
          groupKey: group.key,
          optionKey: value,
          label: optionNode?.label ?? value,
          captureRequired: isCaptureRequiredOption(optionNode, value),
        };
      }
    }
    return null;
  }, [templateGroups, settings.attributes]);

  // GenerationWorkspace.selectTemplateOption と同じ規約:
  // 単一選択を保証し、キャプチャ必須テンプレは GPT Image 2 + プラットフォーム
  // 指定なしを transient で初期適用する (localStorage の選好は汚さない)
  const selectTemplate = (chip: TemplateChip, options?: { logEvent?: boolean }) => {
    const isSelected = activeTemplate?.optionKey === chip.option.key;
    for (const group of templateGroups) {
      if (group.key !== chip.groupKey) setAttribute(group.key, 'none');
    }
    if (isSelected) {
      setAttribute(chip.groupKey, 'none');
      return;
    }
    setAttribute(chip.groupKey, chip.option.key);
    if (isCaptureRequiredOption(chip.option, chip.option.key)) {
      applyTransientSettings(captureTemplateTransientSettings(), CAPTURE_TEMPLATE_TRANSIENT_KEYS);
    }
    // リードは「ユーザーが明示的に選択したとき」だけ記録する。自動復元
    // (pending 復元 / localStorage メモ) では発火させない (ログイン往復での重複計測防止)。
    if (options?.logEvent !== false) {
      logLeadEvent('template_selected', {
        group_key: chip.groupKey,
        option_key: chip.option.key,
        template_name: chip.option.label ?? chip.option.key,
      });
    }
  };

  // ---- キャプチャ必須テンプレの URL 入力補助 ----
  // X / ホームページ の 2 択でプレフィックスを固定し、入力は {} 部分のみに限定する。
  // 完成 URL (プレフィックス + 入力) を templateReferenceUrl に保存する。
  // 入力値は種別ごとに localStorage (URL_MEMO_STORAGE_KEY) にも記憶し、再訪時に復元する。
  const [urlKind, setUrlKind] = useState<UrlKind>('x');
  // 種別ごとの入力値 ({} 部分)。切替時の載せ替えと localStorage 保存の元データ。
  const urlBodiesRef = useRef<Record<UrlKind, string> | null>(null);
  if (urlBodiesRef.current === null) {
    urlBodiesRef.current = readUrlMemo()?.bodies ?? { x: '', instagram: '', homepage: '' };
  }
  const urlBodies: Record<UrlKind, string> = urlBodiesRef.current;

  // url_input リードイベントは 2 秒デバウンス + 直前送信値と同一ならスキップし、
  // 入力途中の 1 文字ごとの断片を送りすぎないようにする。
  const urlInputLogTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastLoggedUrlRef = useRef<string>('');
  const scheduleUrlInputLog = (kind: UrlKind, fullUrl: string) => {
    if (urlInputLogTimerRef.current) clearTimeout(urlInputLogTimerRef.current);
    if (!fullUrl) return;
    urlInputLogTimerRef.current = setTimeout(() => {
      if (fullUrl === lastLoggedUrlRef.current) return;
      lastLoggedUrlRef.current = fullUrl;
      logLeadEvent('url_input', { url_kind: kind, url: fullUrl });
    }, 2000);
  };

  // 初期表示のテンプレート選択 (1 回だけ)。ユーザーが明示的に解除した後は再選択しない。
  // 1. ログイン誘導前に退避した選択 (テンプレ + URL 入力) があれば優先して復元。
  //    削除はログイン済みのときのみ: ログイン前のブラウザバック復帰でも選択を保持する。
  // 2. 無ければ localStorage メモ (前回入力した X ユーザー名 / サイト URL) を復元。
  const autoSelectedRef = useRef(false);
  useEffect(() => {
    if (autoSelectedRef.current || activeTemplate || categories.length === 0) return;
    autoSelectedRef.current = true;
    const pending = readPendingLoginGeneration(isLoggedIn);
    if (pending) {
      const chip = categories
        .flatMap((c) => c.chips)
        .find((c) => c.groupKey === pending.groupKey && c.option.key === pending.optionKey);
      if (chip) {
        selectTemplate(chip, { logEvent: false });
        setTemplateReferenceUrl(pending.templateReferenceUrl);
        setUrlKind(pending.urlKind);
        // ログイン往復で戻した値も種別メモへ反映しておく
        const prefix = URL_PREFIXES[pending.urlKind];
        if (pending.templateReferenceUrl.startsWith(prefix)) {
          urlBodies[pending.urlKind] = pending.templateReferenceUrl.slice(prefix.length);
          writeUrlMemo({ kind: pending.urlKind, bodies: urlBodies });
        }
        return;
      }
    }
    // デフォルトのテンプレート自動選択はしない。ユーザーが「①作りたいものを選ぶ」で
    // 明示的にタップして選ぶ (未選択が初期状態)。前回入力の URL メモのみ復元する。
    const memo = readUrlMemo();
    if (memo) {
      setUrlKind(memo.kind);
      const body = memo.bodies[memo.kind];
      if (body) setTemplateReferenceUrl(URL_PREFIXES[memo.kind] + body);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, activeTemplate]);

  // 入力欄に見せる値 (プレフィックスを剥がした {} 部分)
  const urlBody = (() => {
    const full = templateReferenceUrl.trim();
    const prefix = URL_PREFIXES[urlKind];
    return full.startsWith(prefix) ? full.slice(prefix.length) : '';
  })();

  const applyUrlBody = (kind: UrlKind, body: string) => {
    let trimmed = body.trim();
    // プレフィックス込みの貼り付け (https://x.com/foo / https://example.com) は
    // 二重にならないよう剥がして {} 部分だけを採用する
    const prefix = URL_PREFIXES[kind];
    if (trimmed.toLowerCase().startsWith(prefix)) {
      trimmed = trimmed.slice(prefix.length);
    } else if (kind === 'homepage') {
      trimmed = trimmed.replace(/^https?:\/\//i, '');
    }
    const fullUrl = trimmed ? prefix + trimmed : '';
    setTemplateReferenceUrl(fullUrl);
    urlBodies[kind] = trimmed;
    writeUrlMemo({ kind, bodies: urlBodies });
    scheduleUrlInputLog(kind, fullUrl);
  };

  const changeUrlKind = (kind: UrlKind) => {
    // 種別ごとに記憶した入力値へ切り替える (X のユーザー名とサイト URL は別の値)
    const body = urlBodies[kind];
    setTemplateReferenceUrl(body ? URL_PREFIXES[kind] + body : '');
    setUrlKind(kind);
    writeUrlMemo({ kind, bodies: urlBodies });
  };

  // 未ログインの「画像を生成」: 選択状態を退避してログイン画面へ。
  // /login は AuthGate の PUBLIC_PATHS 外なのでログインフォームが表示され、
  // ログイン成功で '/' へ戻ってきた際に上の復元 effect が選択状態を戻す。
  // 生成の自動実行はしない (ポイント消費を伴うため、復帰後に再クリックさせる)。
  const handleLoginToGenerate = () => {
    if (activeTemplate) {
      stashPendingLoginGeneration({
        groupKey: activeTemplate.groupKey,
        optionKey: activeTemplate.optionKey,
        templateReferenceUrl,
        urlKind,
      });
      // 最重要リード: テンプレ + URL の完全な文脈を持つ。遷移で XHR が切れるため beacon で送る。
      logLeadEventBeacon('login_prompt', {
        group_key: activeTemplate.groupKey,
        option_key: activeTemplate.optionKey,
        url_kind: urlKind,
        url: templateReferenceUrl,
      });
    }
    hardNavigate('/login');
  };

  return (
    <div className="uh-home">
      {/* ポイントバッジをホーム左上に固定。ハンバーガーの「利用状況」と同じく
          利用状況オーバーレイを開く (別ルートへの遷移ではなく統一する)。 */}
      {creditsData && (
        <button
          type="button"
          onClick={() => openOverlay('dashboard')}
          className="uh-points-anchor uh-points-badge"
        >
          <span className="uh-points-number">
            {Math.floor(creditsData.total_available ?? creditsData.credits_balance).toLocaleString()}
          </span>
          <span className="uh-points-unit">ポイント</span>
        </button>
      )}

      {/* ヘッダー無し構成のため、メニューはホーム右上に固定 (モバイルフッターのメニューと同機能) */}
      <div className="uh-menu-anchor" ref={menuRef}>
        <button
          type="button"
          className="uh-menu-button"
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label="メニュー"
          aria-expanded={isMenuOpen}
        >
          <img src="/navigation_menu.svg" alt="" className="uh-menu-icon" />
        </button>
        {isMenuOpen && (
          <div className="modal-surface menu-dropdown">
            <MenuContent
              isAdmin={isAdmin}
              isLoggedIn={!!token}
              onLogout={handleLogout}
              creditsBalance={creditsData ? (creditsData.total_available ?? creditsData.credits_balance) : null}
            />
          </div>
        )}
      </div>

      {/* 利用状況 / マイページのオーバーレイ (戻る導線つき) */}
      <UsodekiPageOverlay />

      <div className="uh-home-inner">
        {/* ロゴ (アプリヘッダーを出さないためホーム側に置く。透過版を紙に直接重ねる) */}
        <div className="uh-brand">
          <img src={usodekiLogoTransparent} alt={brandConfig.serviceName} className="uh-brand-logo" />
        </div>

        {/* org-admin サイト設定「トップページ文言」。未設定なら何も出さない */}
        {(homeHeroTitle || homeHeroSubtitle) && (
          <div className="uh-hero">
            {homeHeroTitle && (
              <h1 className="uh-hero-title">
                {renderAccentText(homeHeroTitle, { className: 'uh-hero-accent' })}
              </h1>
            )}
            {homeHeroSubtitle && <p className="uh-hero-subtitle">{homeHeroSubtitle}</p>}
          </div>
        )}

        {/* ---- STEP 1: テンプレート選択 ---- */}
        <section className="uh-step">
          <div className="uh-step-head">
            <span className="uh-step-no">1</span>
            <h2 className="uh-step-title">作りたいものを選ぶ</h2>
          </div>
          <p className="uh-step-help">まずはタップして選んでね。</p>
          {attributeQuery.isLoading ? (
            <p className="uh-status">読み込み中…</p>
          ) : attributeQuery.isError ? (
            <p className="uh-status">テンプレートの読み込みに失敗しました。時間をおいて再度お試しください。</p>
          ) : categories.length === 0 ? (
            <p className="uh-status">利用できるテンプレートがありません</p>
          ) : (
            categories.map((category) => (
              <div key={category.id} className="uh-tpl-grid">
                {category.chips.map((chip) => {
                  const selected = activeTemplate?.optionKey === chip.option.key;
                  const thumb = thumbnailUrlOf(chip.option);
                  const creator = brandConfig.features.creatorProgram ? chip.option.creator : null;
                  return (
                    <div
                      key={chip.option.id}
                      className={`uh-tpl-tile ${selected ? 'uh-tpl-tile--selected' : ''}`}
                    >
                      {/* button 内に <a> はネストできないため、選択用 button と
                          公開プロフィールへの Link を兄弟要素に分離する。 */}
                      <button
                        type="button"
                        className="uh-tpl-tile-main"
                        onClick={() => selectTemplate(chip)}
                        aria-pressed={selected}
                      >
                        <span className="uh-tpl-tile-icon">
                          {thumb ? (
                            <img src={thumb} alt="" loading="lazy" />
                          ) : (
                            <img src="/design_file_text_image.svg" alt="" className="uh-tpl-tile-glyph" />
                          )}
                          {selected && <span className="uh-tpl-tile-check" aria-hidden="true">✓</span>}
                        </span>
                        <span className="uh-tpl-tile-label">{chip.option.label}</span>
                      </button>
                      {creator && (
                        creator.slug ? (
                          <Link
                            className="uh-tpl-tile-creator uh-tpl-tile-creator--link"
                            to={`/creators/${creator.slug}`}
                            title={`${creator.display_name} のプロフィールを見る`}
                          >
                            <img
                              className="uh-creator-avatar"
                              src={creator.avatar_url || '/usodeki_profile_photo.png'}
                              alt={creator.display_name}
                              loading="lazy"
                            />
                          </Link>
                        ) : (
                          <span className="uh-tpl-tile-creator">
                            <img
                              className="uh-creator-avatar"
                              src={creator.avatar_url || '/usodeki_profile_photo.png'}
                              alt={creator.display_name}
                              loading="lazy"
                            />
                          </span>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </section>

        {/* ---- STEP 2: URL 入力 (キャプチャ必須テンプレ = ポスター等でのみ) ----
            ポスターは初期選択されるため最初から出る。X / ホームページ の 2 択 +
            プレフィックス固定入力 ({} 部分のみ入力) */}
        {activeTemplate?.captureRequired && (
          <section className="uh-step">
            <div className="uh-step-head">
              <span className="uh-step-no">2</span>
              <h2 className="uh-step-title">あなたのプロフィールURL</h2>
            </div>
            <p className="uh-step-help">あなたのプロフィールをAIが読み取って素敵な画像を生成するよ。</p>
            <div className="uh-url-block">
              <div className="uh-url-kind-tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={urlKind === 'x'}
                  title="X"
                  className={`uh-url-kind ${urlKind === 'x' ? 'uh-url-kind--active' : ''}`}
                  onClick={() => changeUrlKind('x')}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817-5.966 6.817H1.68l7.73-8.835L1.254 2.25h6.83l4.713 6.231 5.447-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644Z" />
                  </svg>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={urlKind === 'homepage'}
                  title="ホームページ"
                  className={`uh-url-kind ${urlKind === 'homepage' ? 'uh-url-kind--active' : ''}`}
                  onClick={() => changeUrlKind('homepage')}
                >
                  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M3 12h18" />
                    <path d="M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18Z" />
                  </svg>
                  <span>ホームページ</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={urlKind === 'instagram'}
                  title="Instagram"
                  className={`uh-url-kind ${urlKind === 'instagram' ? 'uh-url-kind--active' : ''}`}
                  onClick={() => changeUrlKind('instagram')}
                >
                  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="2" y="2" width="20" height="20" rx="5.5" />
                    <circle cx="12" cy="12" r="4.2" />
                    <circle cx="17.6" cy="6.4" r="1.1" fill="currentColor" stroke="none" />
                  </svg>
                  <span>Instagram</span>
                </button>
              </div>
              <div className="uh-url-field">
                <span className="uh-url-prefix">{URL_PREFIXES[urlKind]}</span>
                <input
                  type="text"
                  inputMode="url"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  className="uh-url-body-input"
                  placeholder={
                    urlKind === 'homepage'
                      ? 'あなたのホームページURL'
                      : urlKind === 'instagram'
                        ? 'あなたのユーザーネーム'
                        : 'あなたのユーザー名'
                  }
                  value={urlBody}
                  onChange={(e) => applyUrlBody(urlKind, e.target.value)}
                />
              </div>
            </div>
          </section>
        )}

        {/* ---- 生成ボタン + 進行状況 + 生成結果 (機能は共通コンポーネントを再利用) ----
            未ログイン時は同じ見た目の生成ボタンだけを出し、クリックでログインへ誘導する */}
        <div className="uh-generate-area">
          {isLoggedIn ? (
            <GenerationPreviewPanel
              mode="attributes"
              pendingPrompt={pendingPrompt}
              onClearPendingEdit={clearPendingEdit}
              stickyGenerateButton={false}
            />
          ) : (
            <GenerateButton label="画像を生成" onClick={handleLoginToGenerate} />
          )}
        </div>

        {/* アプリ共通フッター (appChrome=false) を出さないため、著作権表示はホーム側に置く */}
        <footer className="uh-footer">
          <span>&copy; {brandConfig.companyName}</span>
        </footer>
      </div>

      {/* 素材パネルは認証必須 API を叩くため未ログイン時は出さない */}
      {isLoggedIn && <MaterialsFloatingOpenButton />}
    </div>
  );
}
